import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

// Per-status text color of the status label. Unknown presign error codes
// fall back to "failed", so a new server-side refusal reason (#40 format
// gate, #35 quota) needs only a label on the server — plus an entry here
// if it should not read as a hard failure (like "duplicate" does).
const STATUS_STYLES = {
  queued: "text-muted-foreground",
  uploading: "text-muted-foreground",
  uploaded: "text-success",
  duplicate: "text-amber-600",
  failed: "text-destructive",
};

// Terminal fill colors of the progress bar. A failed upload keeps its
// current width (it did not finish), only the color flips. The quota
// refusals need their own entries: refused at attach, the bar sits at a
// full primary-colored 100% and must flip red like the status label does.
const BAR_STYLES = {
  uploaded: "bg-success",
  duplicate: "bg-amber-400",
  unsupported_format: "bg-destructive",
  file_too_large: "bg-destructive",
  quota_exceeded: "bg-destructive",
  failed: "bg-destructive",
};

// Everything a bar can wear — cleared before a new color applies, because a
// bar can change color twice: primary → success on upload, success →
// destructive when the async verdict rejects the file.
const BAR_RESET = ["bg-primary", ...new Set(Object.values(BAR_STYLES))];

const ZONE_ACTIVE_CLASSES = ["border-primary", "bg-muted/40"];

// Dropzone (#39): multi-file ActiveStorage direct upload with a per-file
// status report. At most maxConcurrent uploads run at once — a 2000-file
// import queues instead of firing 2000 parallel requests.
//
// Flow per file: presign (POST presignUrl; 409 {error_code: "duplicate"}
// skips the byte upload entirely) → storage PUT with progress → photo
// create (POST createUrl with the signed blob id). Everything after the
// 201 is the live broadcast layer's story (#44): the card appears in the
// grid over the owner's stream, and an async format-gate rejection
// arrives as the `dropzone:verdict` CustomEvent the server dispatches on
// this controller's root (see Components::Dropzone.broadcast_verdict) —
// verdictBroadcasted() flips the matching report row.
export default class extends Controller {
  static targets = ["zone", "input", "list", "rowTemplate"];
  static values = {
    presignUrl: String,
    createUrl: String,
    maxConcurrent: { type: Number, default: 3 },
    messages: Object,
  };

  initialize() {
    this.queue = [];
    this.active = 0;
  }

  browse() {
    this.inputTarget.click();
  }

  inputChanged() {
    this.enqueue(this.inputTarget.files);
    // Selecting the same file again must fire change again — e.g. retrying
    // after a failure, or re-checking a file against the library.
    this.inputTarget.value = "";
  }

  dragOver(event) {
    event.preventDefault();
    this.zoneTarget.classList.add(...ZONE_ACTIVE_CLASSES);
  }

  dragLeave() {
    this.zoneTarget.classList.remove(...ZONE_ACTIVE_CLASSES);
  }

  drop(event) {
    event.preventDefault();
    this.dragLeave();
    this.enqueue(event.dataTransfer.files);
  }

  enqueue(fileList) {
    for (const file of fileList) {
      this.queue.push({ file, row: this.appendRow(file) });
    }
    this.pump();
  }

  pump() {
    while (this.active < this.maxConcurrentValue && this.queue.length > 0) {
      this.start(this.queue.shift());
    }
  }

  start(entry) {
    this.active += 1;
    this.setStatus(entry, "uploading");
    const upload = new DirectUpload(
      entry.file,
      this.presignUrlValue,
      this.delegateFor(entry),
    );
    upload.create((error, blob) => {
      if (error) {
        this.finish(entry, this.presignErrorCode(entry) || "failed");
      } else {
        this.createPhoto(entry, blob);
      }
    });
  }

  // @rails/activestorage collapses any non-2xx presign into an error string,
  // so the machine-readable refusal code ({error_code: "duplicate", …})
  // comes off the presign XHR the delegate kept hold of.
  presignErrorCode(entry) {
    const xhr = entry.presignXhr;
    if (!xhr || xhr.status === 0 || (xhr.status >= 200 && xhr.status < 300)) {
      return undefined;
    }
    return xhr.response?.error_code;
  }

  delegateFor(entry) {
    return {
      directUploadWillCreateBlobWithXHR: (xhr) => {
        entry.presignXhr = xhr;
      },
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener("progress", (event) => {
          if (!event.lengthComputable) return;
          this.setProgress(entry, (event.loaded / event.total) * 100);
        });
      },
    };
  }

  async createPhoto(entry, blob) {
    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({ photo: { original: blob.signed_id } }),
      });
      if (response.status === 201) {
        // Correlate the report row with the photo it produced: the async
        // verdict broadcast (#44) addresses it by this id when the format
        // gate rejects after "Uploaded".
        const { id } = await response.json();
        if (id) {
          entry.row.id = `photo_report_${id}`;
          entry.row.dataset.photoId = id;
        }
        this.setProgress(entry, 100);
        this.finish(entry, "uploaded");
      } else {
        // Attach refusals resolve like presign refusals: a machine-readable
        // error_code picks its localized status label (quota_exceeded,
        // file_too_large), anything else degrades to "failed".
        const body = await this.errorBody(response);
        this.finish(entry, body.error_code || "failed", body.error);
      }
    } catch {
      this.finish(entry, "failed");
    }
  }

  // The server's refusals carry an {error_code:} and/or a localized
  // full-sentence `error` (the latter wins as the row text when present).
  async errorBody(response) {
    try {
      return await response.json();
    } catch {
      return {};
    }
  }

  finish(entry, status, detail = undefined) {
    if (status === "duplicate") this.setProgress(entry, 100);
    this.setStatus(entry, status, detail);
    this.active -= 1;
    this.pump();
  }

  // The broadcast verdict (#44): the server dispatched `dropzone:verdict`
  // on this root with {id, verdict, reason}. Rows are addressed by the
  // photo id their create response carried; a verdict for a photo this
  // session's report does not hold (uploaded in another tab, row already
  // settled) is silently nobody's business — the durable rejected section
  // under the grid is the record of record either way.
  verdictBroadcasted(event) {
    const { id, verdict, reason } = event.detail || {};
    if (verdict !== "rejected") return;
    const row = this.element.querySelector(
      `[data-photo-id="${CSS.escape(String(id))}"]:not([data-verdict])`,
    );
    if (!row) return;
    row.dataset.verdict = verdict;
    // The localized full-sentence reason wins as the row text; a null
    // reason (stale key) degrades to the generic "not a 360 photo" label.
    // Both read destructive — the styles fall back to failed.
    this.setRowStatusText(row, "unsupported_format", reason);
    this.applyBarStyle(row, BAR_STYLES.unsupported_format);
  }

  appendRow(file) {
    const fragment = this.rowTemplateTarget.content.cloneNode(true);
    const row = fragment.querySelector("li");
    row.querySelector('[data-slot="name"]').textContent = file.name;
    this.setRowStatusText(row, "queued");
    this.listTarget.append(row);
    this.listTarget.hidden = false;
    return row;
  }

  setStatus(entry, status, detail = undefined) {
    this.setRowStatusText(entry.row, status, detail);
    const barStyle = BAR_STYLES[status];
    if (barStyle) this.applyBarStyle(entry.row, barStyle);
  }

  applyBarStyle(row, style) {
    const bar = row.querySelector('[data-slot="bar"]');
    bar.classList.remove(...BAR_RESET);
    bar.classList.add(style);
  }

  setRowStatusText(row, status, detail = undefined) {
    const statuses = this.messagesValue.statuses;
    const statusElement = row.querySelector('[data-slot="status"]');
    statusElement.textContent = detail || statuses[status] || statuses.failed;
    statusElement.className = `shrink-0 text-xs ${STATUS_STYLES[status] || STATUS_STYLES.failed}`;
  }

  setProgress(entry, percent) {
    entry.row.querySelector('[data-slot="bar"]').style.width = `${percent}%`;
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }
}
